import React from 'react';
import { useLanguage } from '../LanguageContext';
import { CONTENT } from '../content';
import EarningsCalculator from '../components/EarningsCalculator';
import EarningsLadder from '../components/EarningsLadder';
import QRCodeBlock from '../components/QRCodeBlock';
import WhyJoin from '../components/WhyJoin';

const MoneyFlowDiagram = ({ text }: { text: any }) => (
  <svg viewBox="0 0 1000 450" className="w-full h-auto max-w-6xl mx-auto drop-shadow-lg font-sans">
    <defs>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#568490" />
      </marker>
    </defs>
    
    {/* CMS Node - Left Aligned */}
    <rect x="10" y="150" width="120" height="80" rx="8" fill="#27201A" />
    <text x="70" y="185" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">{text.cms}</text>
    <text x="70" y="205" textAnchor="middle" fill="#ccc" fontSize="10">(Medicare Advantage)</text>
    
    {/* Arrow 1: CMS -> Plan (Widened Gap) */}
    <line x1="130" y1="190" x2="330" y2="190" stroke="#568490" strokeWidth="3" markerEnd="url(#arrowhead)" />
    <text x="230" y="175" textAnchor="middle" fontSize="13" fontWeight="800" fill="#1f2937">{text.premium}</text>
    <text x="230" y="210" textAnchor="middle" fontSize="11" fontWeight="500" fill="#4b5563">PMPM</text>

    {/* Health Plan Node - Shifted Right */}
    <rect x="330" y="150" width="140" height="80" rx="8" fill="#568490" />
    <text x="400" y="195" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">{text.plan}</text>

    {/* Arrow 2: Plan -> Suncoast (Widened Gap) */}
    <line x1="470" y1="190" x2="670" y2="190" stroke="#568490" strokeWidth="3" markerEnd="url(#arrowhead)" />
    <text x="570" y="175" textAnchor="middle" fontSize="13" fontWeight="800" fill="#1f2937">{text.cap}</text>

    {/* Suncoast Node - Shifted Right */}
    <rect x="670" y="100" width="300" height="180" rx="8" fill="#2F6B77" />
    <text x="820" y="130" textAnchor="middle" fill="white" fontWeight="800" fontSize="18">Suncoast MD</text>
    <text x="820" y="150" textAnchor="middle" fill="#D2E6E7" fontSize="12" fontWeight="500">{text.mso}</text>
    
    <line x1="690" y1="170" x2="950" y2="170" stroke="white" strokeWidth="1" strokeDasharray="4"/>
    <text x="820" y="200" textAnchor="middle" fill="white" fontSize="12">{text.claims}</text>
    <text x="820" y="220" textAnchor="middle" fill="#D2E6E7" fontSize="10">(Reinsurance/Stop-Loss)</text>

    {/* Arrow 3: Suncoast -> Practice (Vertical) - Centered on new Suncoast position */}
    <line x1="820" y1="280" x2="820" y2="330" stroke="#D9924D" strokeWidth="4" markerEnd="url(#arrowhead)" />
    
    {/* Surplus Label */}
    <text x="835" y="315" textAnchor="start" fill="#2F6B77" fontWeight="bold" fontSize="13">{text.surplus}</text>

    {/* Physician Node - Centered on Arrow */}
    <rect x="730" y="340" width="180" height="80" rx="8" fill="#D9924D" />
    <text x="820" y="375" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">{text.practice}</text>
    <text x="820" y="395" textAnchor="middle" fill="white" fontSize="10">{text.practiceSub || "PMPM + Surplus"}</text>
  </svg>
);

export default function Economics() {
  const { language } = useLanguage();
  const t = CONTENT[language].economics;
  const homeT = CONTENT[language].home;
  const common = CONTENT[language].common;

  return (
    <div className="bg-white">
      <div className="bg-suncoast-secondary py-16">
        <div className="container mx-auto px-4 lg:px-12 text-center text-white">
          <h1 className="text-4xl font-bold mb-4">{t.title}</h1>
          <p className="max-w-2xl mx-auto text-lg opacity-90">{t.subtitle}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 lg:px-12 py-16">
        
        {/* Earnings Ladder Expansion */}
        <div className="mb-20 max-w-4xl mx-auto">
           <EarningsLadder />
        </div>

        {/* Calculator Section */}
        <div className="mb-20 grid lg:grid-cols-2 gap-12 items-center">
           <div>
              <h2 className="text-3xl font-bold text-suncoast-primary mb-6">{homeT.earningsConcept}</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t.timeHorizons.start} 
                <strong className="text-suncoast-primary">{t.timeHorizons.highlight}</strong>
                {t.timeHorizons.end}
              </p>
              <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                <h4 className="font-bold text-blue-800 mb-2">{t.keyDefinitions.title}</h4>
                <ul className="text-sm text-blue-700 space-y-2">
                  <li><strong>{t.keyDefinitions.raf.term}</strong>{t.keyDefinitions.raf.def}</li>
                  <li><strong>{t.keyDefinitions.hedis.term}</strong>{t.keyDefinitions.hedis.def}</li>
                  <li><strong>{t.keyDefinitions.toc.term}</strong>{t.keyDefinitions.toc.def}</li>
                </ul>
              </div>
           </div>
           <div>
              <EarningsCalculator />
           </div>
        </div>

        {/* Money Flow */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-suncoast-dark mb-8 text-center">{t.flowTitle}</h2>
          <div className="bg-gray-50 p-8 rounded-xl border border-gray-100 mb-8 overflow-x-auto">
            <MoneyFlowDiagram text={t.diagram} />
          </div>
          <div className="max-w-3xl mx-auto text-gray-600 space-y-4">
             <p>{t.flowDesc1}</p>
             <p><strong>{t.flowDesc2}</strong></p>
          </div>
        </div>

        {/* Models Comparison */}
        <div className="grid md:grid-cols-2 gap-12 mb-20">
          <div className="p-8 border rounded-xl hover:shadow-lg transition-shadow">
            <h3 className="text-xl font-bold text-suncoast-primary mb-4">{t.models.shared.title}</h3>
            <p className="text-gray-600 mb-4">{t.models.shared.desc}</p>
            <ul className="space-y-3">
              <li className="flex gap-2 text-sm">
                <span className="font-bold text-green-600">Income:</span>
                <span>{t.models.shared.income}</span>
              </li>
              <li className="flex gap-2 text-sm">
                <span className="font-bold text-green-600">Bonus:</span>
                <span>{t.models.shared.bonus}</span>
              </li>
              <li className="flex gap-2 text-sm">
                <span className="font-bold text-red-500">Risk:</span>
                <span>{t.models.shared.risk}</span>
              </li>
            </ul>
          </div>
          <div className="p-8 border rounded-xl hover:shadow-lg transition-shadow bg-suncoast-offWhite">
            <h3 className="text-xl font-bold text-suncoast-primary mb-4">{t.models.capitation.title}</h3>
            <p className="text-gray-600 mb-4">{t.models.capitation.desc}</p>
            <ul className="space-y-3">
              <li className="flex gap-2 text-sm">
                <span className="font-bold text-green-600">Income:</span>
                <span>{t.models.capitation.income}</span>
              </li>
              <li className="flex gap-2 text-sm">
                <span className="font-bold text-green-600">Bonus:</span>
                <span>{t.models.capitation.bonus}</span>
              </li>
              <li className="flex gap-2 text-sm">
                <span className="font-bold text-red-500">Risk:</span>
                <span>{t.models.capitation.risk}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Why Join Module */}
        <div className="mb-20">
          <WhyJoin />
        </div>
        
        {/* Compliance Footer */}
        <div className="p-6 bg-suncoast-bg/30 rounded-lg text-sm text-gray-500 flex flex-col md:flex-row items-center gap-6">
           <div className="flex-grow">
             <h4 className="font-bold mb-2">{t.complianceTitle}</h4>
             <p>{t.complianceDesc}</p>
           </div>
           <div>
             <QRCodeBlock />
           </div>
        </div>
      </div>
    </div>
  );
}